import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

import {
	ConditionValue,
	ConditionValueKind,
	TIME_UNITS,
	TimeUnit,
	isBooleanType,
} from "../../utils/conditions";

export interface FieldOption {
	name: string;
	type?: string;
}

interface ValueEditorProps {
	value: ConditionValue | undefined;
	onChange: (value: ConditionValue) => void;
	/** Type of the rule's selected field (drives boolean input + defaults). */
	fieldType?: string;
	/** Fields offered for the "variable" / "field ± offset" value kinds. */
	fields: FieldOption[];
}

const KIND_OPTIONS: ConditionValueKind[] = [
	"literal",
	"variable",
	"datetime",
	"nowRelative",
	"varRelative",
];

const defaultForKind = (kind: ConditionValueKind, fields: FieldOption[]): ConditionValue => {
	switch (kind) {
		case "literal":
			return { kind: "literal", value: "" };
		case "variable":
			return { kind: "variable", name: fields[0]?.name ?? "" };
		case "datetime":
			return { kind: "datetime", iso: "" };
		case "nowRelative":
			return { kind: "nowRelative", offset: 0, unit: "day" };
		case "varRelative":
			return { kind: "varRelative", varName: fields[0]?.name ?? "", offset: 0, unit: "day" };
	}
};

/** A signed offset editor: sign dropdown + magnitude + unit dropdown. */
const OffsetFields = ({
	offset,
	unit,
	onChange,
}: {
	offset: number;
	unit: TimeUnit;
	onChange: (offset: number, unit: TimeUnit) => void;
}) => {
	const sign = offset < 0 ? "-" : "+";
	const magnitude = Math.abs(offset);
	return (
		<>
			<Dropdown
				className="cb-sign"
				value={sign}
				options={[
					{ label: "+", value: "+" },
					{ label: "−", value: "-" },
				]}
				onChange={(event) => onChange((event.value === "-" ? -1 : 1) * magnitude, unit)}
			/>
			<InputText
				className="cb-offset"
				value={String(magnitude)}
				keyfilter="pint"
				onChange={(event) => {
					const next = event.target.value ? parseInt(event.target.value, 10) : 0;
					onChange((sign === "-" ? -1 : 1) * next, unit);
				}}
			/>
			<Dropdown
				className="cb-unit"
				value={unit}
				options={TIME_UNITS.map((u) => ({ label: u, value: u }))}
				onChange={(event) => onChange(offset, event.value)}
			/>
		</>
	);
};

/**
 * Edits the right-hand side of a condition rule. A small "kind" selector lets
 * the user choose between a literal, another field, a date, NOW()±offset, or a
 * field±offset — mirroring every RHS form the DSL accepts.
 */
const ValueEditor = ({ value, onChange, fieldType, fields }: ValueEditorProps) => {
	const { t } = useTranslation("common");
	const kind: ConditionValueKind = value?.kind ?? "literal";

	const fieldOptions = fields.map((field) => ({
		label: field.type ? `${field.name} (${field.type})` : field.name,
		value: field.name,
	}));

	return (
		<div className="cb-value">
			<Dropdown
				className="cb-value-kind"
				value={kind}
				options={KIND_OPTIONS.map((option) => ({
					label: t(`MARKETING_CAMPAIGNS.CONDITIONS.VALUE_KIND.${option}`),
					value: option,
				}))}
				onChange={(event) => onChange(defaultForKind(event.value, fields))}
			/>

			{kind === "literal" &&
				(isBooleanType(fieldType) ? (
					<Dropdown
						className="cb-value-input"
						value={value?.kind === "literal" ? value.value : ""}
						options={[
							{ label: "True", value: "True" },
							{ label: "False", value: "False" },
						]}
						placeholder={t("MARKETING_CAMPAIGNS.CONDITIONS.VALUE_PLACEHOLDER")}
						onChange={(event) => onChange({ kind: "literal", value: event.value })}
					/>
				) : (
					<InputText
						className="cb-value-input"
						value={value?.kind === "literal" ? value.value : ""}
						placeholder={t("MARKETING_CAMPAIGNS.CONDITIONS.VALUE_PLACEHOLDER")}
						onChange={(event) =>
							onChange({ kind: "literal", value: event.target.value })
						}
					/>
				))}

			{kind === "variable" && (
				<Dropdown
					className="cb-value-input"
					value={value?.kind === "variable" ? value.name : ""}
					options={fieldOptions}
					editable
					filter
					placeholder={t("MARKETING_CAMPAIGNS.CONDITIONS.FIELD_PLACEHOLDER")}
					onChange={(event) => onChange({ kind: "variable", name: event.value })}
				/>
			)}

			{kind === "datetime" && (
				<InputText
					className="cb-value-input"
					value={value?.kind === "datetime" ? value.iso : ""}
					placeholder="2026-02-12T00:00:00"
					onChange={(event) => onChange({ kind: "datetime", iso: event.target.value })}
				/>
			)}

			{kind === "nowRelative" && value?.kind === "nowRelative" && (
				<div className="cb-offset-row">
					<span className="cb-now-label">NOW()</span>
					<OffsetFields
						offset={value.offset}
						unit={value.unit}
						onChange={(offset, unit) => onChange({ kind: "nowRelative", offset, unit })}
					/>
				</div>
			)}

			{kind === "varRelative" && value?.kind === "varRelative" && (
				<div className="cb-offset-row">
					<Dropdown
						className="cb-value-input"
						value={value.varName}
						options={fieldOptions}
						editable
						filter
						placeholder={t("MARKETING_CAMPAIGNS.CONDITIONS.FIELD_PLACEHOLDER")}
						onChange={(event) =>
							onChange({ ...value, kind: "varRelative", varName: event.value })
						}
					/>
					<OffsetFields
						offset={value.offset}
						unit={value.unit}
						onChange={(offset, unit) =>
							onChange({ kind: "varRelative", varName: value.varName, offset, unit })
						}
					/>
				</div>
			)}
		</div>
	);
};

export default ValueEditor;
